import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate, type GroupSummaryDto } from '@pb/shared';
import { createGroup, listGroups, retireGroup, unretireGroup } from '../api/groups';
import { Dialog } from '../components/Dialog';
import { RetireDialog } from '../components/RetireDialog';
import { EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { TagEditorDialog, TagFilter, TagList } from '../components/Tags';
import { ThumbLink } from '../components/Thumb';
import { useProject } from '../lib/project';
import { anyPending } from '../lib/thumbs';
import { pageCountLabel, pluralize } from '../lib/text';
import { usePolling, useResource } from '../lib/useResource';

export function NameDescriptionDialog({
  title,
  submitLabel,
  initialName = '',
  initialDescription = '',
  withSubtitle = false,
  initialSubtitle = '',
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  submitLabel: string;
  initialName?: string;
  initialDescription?: string;
  withSubtitle?: boolean;
  initialSubtitle?: string;
  onClose: () => void;
  onSubmit: (name: string, description: string, subtitle: string) => Promise<void>;
  children?: ReactNode;
}) {
  const [name, setName] = useState(initialName);
  const [subtitle, setSubtitle] = useState(initialSubtitle);
  const [description, setDescription] = useState(initialDescription);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(name.trim(), description, subtitle.trim());
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={title}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="name-description-form" className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : submitLabel}
          </button>
        </>
      }
    >
      <form
        id="name-description-form"
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <label htmlFor="nd-name">Name</label>
          <input
            id="nd-name"
            className="input"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </div>
        {withSubtitle ? (
          <div className="field">
            <label htmlFor="nd-subtitle">Subtitle</label>
            <input
              id="nd-subtitle"
              className="input"
              value={subtitle}
              maxLength={300}
              onChange={(event) => setSubtitle(event.target.value)}
            />
            <span className="field-hint">Optional. Replaces {'{{presentation.subtitle}}'} in the template.</span>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="nd-description">Description</label>
          <textarea
            id="nd-description"
            className="textarea"
            value={description}
            maxLength={4000}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        {children}
      </form>
      <ErrorAlert error={error} />
    </Dialog>
  );
}

export function GroupsPage() {
  const { project } = useProject();
  const navigate = useNavigate();
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [dialog, setDialog] = useState<'tags' | 'retire' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [unretiring, setUnretiring] = useState(false);
  const groups = useResource(
    () => listGroups(project.id, tagIds, includeRetired),
    [project.id, tagIds.join(','), includeRetired],
  );
  const all = groups.data ?? [];
  usePolling(anyPending(all.map((g) => g.thumbnail)), () => void groups.refresh());
  const needle = search.trim().toLowerCase();
  const list = needle
    ? all.filter((g) => g.name.toLowerCase().includes(needle) || g.description.toLowerCase().includes(needle))
    : all;

  const toggle = (id: number) =>
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  const visibleSelected = selected.filter((id) => list.some((g) => g.id === id));
  const selectedGroups: GroupSummaryDto[] = list.filter((g) => visibleSelected.includes(g.id));
  const commonTags = selectedGroups.length
    ? (selectedGroups[0]?.tags ?? [])
        .map((t) => t.id)
        .filter((id) => selectedGroups.every((g) => g.tags.some((t) => t.id === id)))
    : [];
  const activeSelected = selectedGroups.filter((g) => !g.retired);
  const retiredSelected = selectedGroups.filter((g) => g.retired);

  const afterGroupsChange = () => {
    setSelected([]);
    void groups.refresh();
  };

  const unretireSelected = async () => {
    setUnretiring(true);
    setActionError(null);
    setNotice(null);
    let done = 0;
    let firstError: unknown = null;
    for (const group of retiredSelected) {
      try {
        await unretireGroup(group.id);
        done += 1;
      } catch (err) {
        firstError ??= err;
      }
    }
    setUnretiring(false);
    if (done > 0) setNotice(`${pluralize(done, 'group')} unretired.`);
    if (firstError) setActionError(firstError);
    afterGroupsChange();
  };

  return (
    <div className="section">
      <div className="page-header">
        <div className="page-title">
          <h2>Groups</h2>
          <p className="muted small">Ordered sets of slides that presentations reuse. Every presentation shows the latest version.</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            New group
          </button>
        </div>
      </div>
      <div className="toolbar">
        <input
          type="search"
          className="input input-search"
          style={{ width: 320 }}
          placeholder="Filter by name"
          aria-label="Filter groups by name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(event) => setIncludeRetired(event.target.checked)}
            aria-label="Show retired"
          />
          Show retired
        </label>
        <span className="spacer" />
        {list.length > 0 ? (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => setSelected(visibleSelected.length === list.length ? [] : list.map((g) => g.id))}
          >
            {visibleSelected.length === list.length ? 'Clear selection' : 'Select all'}
          </button>
        ) : null}
      </div>
      <TagFilter selected={tagIds} onChange={setTagIds} />
      {notice ? (
        <div className="alert alert-success row-between" role="status">
          <span>{notice}</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      <ErrorAlert error={actionError} />
      {groups.error ? (
        <ErrorState error={groups.error} onRetry={() => void groups.reload()} />
      ) : groups.data === undefined ? (
        <Loading label="Loading groups…" />
      ) : list.length === 0 ? (
        all.length === 0 && tagIds.length === 0 ? (
          <EmptyState
            title="No groups yet"
            action={
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                Create a group
              </button>
            }
          >
            Create a group here, or select slides in the library and add them to a new group.
          </EmptyState>
        ) : (
          <EmptyState title="No groups match">Change the filter or the tag selection.</EmptyState>
        )
      ) : (
        <div className="grid-cards">
          {list.map((g) => (
            <article key={g.id} className={`tile${visibleSelected.includes(g.id) ? ' tile-selected' : ''}`}>
              <label className="tile-select checkbox">
                <input
                  type="checkbox"
                  checked={visibleSelected.includes(g.id)}
                  onChange={() => toggle(g.id)}
                  aria-label={`Select ${g.name}`}
                />
              </label>
              <ThumbLink
                to={`/projects/${project.id}/groups/${g.id}`}
                thumb={g.thumbnail}
                alt={g.name}
                badge={pluralize(g.slide_count, 'slide')}
                onRetried={() => void groups.refresh()}
              />
              <Link to={`/projects/${project.id}/groups/${g.id}`} className="tile-title" title={g.name}>
                {g.name}
              </Link>
              <div className="row-wrap">
                {g.retired ? <span className="badge badge-warning">Retired</span> : null}
                <span className="xsmall faint">
                  v{g.version_no} · {pageCountLabel(g.page_count, g.hidden_page_count)} · {formatDate(g.updated_at)}
                </span>
              </div>
              {g.description ? <p className="small muted">{g.description}</p> : null}
              <TagList tags={g.tags} />
            </article>
          ))}
        </div>
      )}
      {visibleSelected.length > 0 ? (
        <div className="selection-bar" role="region" aria-label="Selection actions">
          <strong>{pluralize(visibleSelected.length, 'group')} selected</strong>
          <span className="spacer" />
          <button type="button" className="btn btn-sm" onClick={() => setDialog('tags')}>
            Set tags…
          </button>
          {activeSelected.length > 0 ? (
            <button type="button" className="btn btn-sm" onClick={() => setDialog('retire')} disabled={unretiring}>
              Retire…
            </button>
          ) : null}
          {includeRetired && retiredSelected.length > 0 ? (
            <button type="button" className="btn btn-sm" onClick={() => void unretireSelected()} disabled={unretiring}>
              {unretiring ? 'Unretiring…' : 'Unretire'}
            </button>
          ) : null}
          <button type="button" className="btn btn-sm" onClick={() => setSelected([])}>
            Clear
          </button>
        </div>
      ) : null}
      {dialog === 'tags' ? (
        <TagEditorDialog
          target="group"
          ids={visibleSelected}
          initial={commonTags}
          title={`Set tags on ${pluralize(visibleSelected.length, 'group')}`}
          hint="The chosen tags replace the current tags of every selected group."
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            setNotice('Tags saved.');
            void groups.refresh();
          }}
        />
      ) : null}
      {dialog === 'retire' ? (
        <RetireDialog
          noun="group"
          items={activeSelected.map((g) => ({ id: g.id, name: g.name, to: `/projects/${project.id}/groups/${g.id}` }))}
          description="Retired groups are hidden unless “Show retired” is on, and can be unretired at any time. A group used in a presentation that is not retired is refused and listed afterwards."
          inUseLead="It is used in these presentations:"
          retire={(item) => retireGroup(item.id, item.name)}
          onClose={() => setDialog(null)}
          onFinished={(retired) => {
            setActionError(null);
            setNotice(retired > 0 ? `${pluralize(retired, 'group')} retired.` : null);
            afterGroupsChange();
          }}
        />
      ) : null}
      {creating ? (
        <NameDescriptionDialog
          title="New group"
          submitLabel="Create group"
          onClose={() => setCreating(false)}
          onSubmit={async (name, description) => {
            const created = await createGroup(project.id, { name, description });
            navigate(`/projects/${project.id}/groups/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}
